
import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { normalizeColumnKind } from '../../state/tickets/tickets.models';

type KanbanCard = {
  id: string;
  ticketNumber?: number;
  title: string;
  description?: string;
  agentify?: {
    status?: 'completed' | 'failed';
    source?: 'auto' | 'manual';
    lastAttemptAt?: number;
    completedAt?: number;
    error?: string;
  };
  feedback?: Array<{
    source?: string;
    kind?: string;
    summary: string;
    at?: number;
  }>;
  pr?: {
    status?: string;
    reviewState?: string;
    url?: string;
  };
};

type KanbanColumn = {
  id: string;
  title: string;
  kind?: 'backlog' | 'execute' | 'review' | 'done' | 'custom';
  cards: KanbanCard[];
  widthPx?: number;
};

@Component({
  selector: 'app-kanban-board',
  standalone: true,
  imports: [NgbTooltip],
  templateUrl: './kanban-board.component.html',
  styleUrls: ['./kanban-board.component.scss'],
})
export class KanbanBoardComponent {
  @Input() storageKey: string | null = 'kanban.board.v1';
  @Input() initialColumns: KanbanColumn[] | null = null;
  @Input() agentifyEnabled = false;
  @Input() agentifyingCardIds: string[] = [];
  @Input() resumableCardIds: string[] = [];
  @Output() boardChange = new EventEmitter<KanbanColumn[]>();
  /**
   * Emits when the user performs a "commit" action (e.g. saving a card edit,
   * deleting/moving items, etc). This is the right signal to persist to DB.
   */
  @Output() boardCommit = new EventEmitter<KanbanColumn[]>();
  /**
   * Emits when a user clicks a card (useful for side-panels / execution actions).
   */
  @Output() cardSelected = new EventEmitter<{ columnId: string; card: KanbanCard }>();
  /**
   * Emits when a card is dropped into a (possibly different) column.
   */
  @Output() cardDropped = new EventEmitter<{ card: KanbanCard; fromColumnId: string; toColumnId: string }>();
  @Output() agentifyCard = new EventEmitter<{ columnId: string; card: KanbanCard }>();
  @Output() resumeCard = new EventEmitter<{ columnId: string; card: KanbanCard }>();

  columns: KanbanColumn[] = [];
  private initialized = false;
  private suppressEmit = false;
  private dirty = false;
  private draftCardIds = new Set<string>();
  private transparentDragImage: HTMLImageElement | null = null;

  // Editing state
  editingColumnId: string | null = null;
  editingCardId: string | null = null;
  selectedCardId: string | null = null;
  selectedColumnId: string | null = null;

  // Column resize state
  resizingColumnId: string | null = null;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizeMoveListener?: (e: PointerEvent) => void;
  private resizeUpListener?: (e: PointerEvent) => void;
  private resizeCancelListener?: (e: PointerEvent) => void;
  private resizeMouseMoveListener?: (e: MouseEvent) => void;
  private resizeMouseUpListener?: (e: MouseEvent) => void;
  private activeResizePointerId: number | null = null;

  // Sizing constraints
  private readonly minColumnWidthPx = 260;
  private readonly maxColumnWidthPx = 5000;
  private readonly defaultColumnWidthPx = 320;

  prStatusLabel(card: KanbanCard): string | null {
    const status = String(card?.pr?.status ?? '').trim();
    if (!status) return null;
    return status.replace(/_/g, ' ');
  }

  prStatusBadgeClass(card: KanbanCard): string {
    const status = String(card?.pr?.status ?? '').trim().toLowerCase();
    if (status === 'merged') return 'text-bg-success';
    if (status === 'checks_passed') return 'text-bg-success';
    if (status === 'checks_failed') return 'text-bg-danger';
    if (status === 'review_changes_requested') return 'text-bg-danger';
    if (status === 'review_approved') return 'text-bg-primary';
    if (status === 'pr_open' || status === 'review_comment') return 'text-bg-info';
    return 'text-bg-secondary';
  }

  latestFeedback(card: KanbanCard): string | null {
    const items = Array.isArray(card?.feedback) ? card.feedback : [];
    const last = items.length > 0 ? items[items.length - 1] : null;
    const summary = String(last?.summary ?? '').trim();
    return summary || null;
  }

  isAgentifyingCard(card: KanbanCard): boolean {
    const id = String(card?.id ?? '').trim();
    return !!id && (this.agentifyingCardIds ?? []).some((entry) => String(entry ?? '').trim() === id);
  }

  canAgentifyCard(column: KanbanColumn, card: KanbanCard): boolean {
    if (!this.agentifyEnabled) return false;
    if (!column || !card) return false;
    return String(column?.title ?? '').trim().toLowerCase() === 'feedback';
  }

  agentifyStatusLabel(card: KanbanCard): string | null {
    if (this.isAgentifyingCard(card)) return 'Agentifying...';
    if (String(card?.agentify?.status ?? '').trim().toLowerCase() === 'failed') {
      return String(card?.agentify?.error ?? '').trim() || 'Agentify failed. Retry available.';
    }
    return null;
  }

  agentifyTooltip(card: KanbanCard): string {
    return String(card?.agentify?.status ?? '').trim().toLowerCase() === 'failed'
      ? 'Retry ticket agentification'
      : 'Convert feedback into a backlog-ready ticket';
  }

  onAgentifyClick(column: KanbanColumn, card: KanbanCard, event: Event): void {
    event.stopPropagation();
    if (!this.canAgentifyCard(column, card) || this.isAgentifyingCard(card)) return;
    this.agentifyCard.emit({ columnId: String(column?.id ?? ''), card });
  }

  canResumeCard(column: KanbanColumn, card: KanbanCard): boolean {
    if (!column || !card) return false;
    const kind = normalizeColumnKind((column as any)?.kind, column?.title);
    if (kind === 'review' || kind === 'done') return false;
    const id = String(card?.id ?? '').trim();
    return !!id && (this.resumableCardIds ?? []).some((entry) => String(entry ?? '').trim() === id);
  }

  onResumeClick(column: KanbanColumn, card: KanbanCard, event: Event): void {
    event.stopPropagation();
    if (!this.canResumeCard(column, card)) return;
    this.resumeCard.emit({ columnId: String(column?.id ?? ''), card });
  }

  ngOnInit() {
    this.load();
    if (!this.columns.length) {
      const incoming = (this.initialColumns && this.initialColumns.length > 0)
        ? this.cloneColumns(this.initialColumns)
        : null;
      this.columns = incoming ?? [
        { id: this.generateId(), title: 'Backlog', cards: [] },
        { id: this.generateId(), title: 'Execute', cards: [] },
        { id: this.generateId(), title: 'In Progress', cards: [] },
        { id: this.generateId(), title: 'Review', cards: [] },
        { id: this.generateId(), title: 'Done', cards: [] },
      ];
      // Do not emit/save on init; parent may provide DB state shortly after init.
    }
    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If parent provides initialColumns after init (e.g. loaded from DB),
    // hydrate the board as long as we aren't already showing a board.
    if (changes['initialColumns']) {
      const next = this.initialColumns;
      if (next && next.length > 0) {
        // Only hydrate from parent while we are not "dirty" (user hasn't edited yet),
        // unless the parent is controlling persistence (storageKey === null), in which case
        // we should accept programmatic updates (e.g. AI-generated boards).
        if (this.dirty && this.storageKey !== null) return;

        const shouldHydrate = (() => {
          if (!this.columns.length || !this.initialized) return true;
          if (this.storageKey === null) {
            // Controlled mode: update whenever incoming differs.
            try {
              return JSON.stringify(this.columns) !== JSON.stringify(next);
            } catch {
              return true;
            }
          }
          return false;
        })();

        if (shouldHydrate) {
          this.suppressEmit = true;
          this.columns = this.cloneColumns(next);
          if (this.storageKey) {
            try {
              localStorage.setItem(this.storageKey, JSON.stringify(this.columns));
            } catch {}
          }
          this.suppressEmit = false;
        }
      }
    }
  }

  // Basic persistence
  private load() {
    if (!this.storageKey) return;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (raw) this.columns = JSON.parse(raw);
    } catch {}
  }

  private persist() {
    this.dirty = true;
    if (!this.suppressEmit) this.boardChange.emit(this.columns);
    if (!this.storageKey) return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.columns));
    } catch {}
  }

  private commit() {
    this.persist();
    // Emit a cloned snapshot for consumers to persist safely.
    this.boardCommit.emit(this.cloneColumns(this.columns));
  }

  // Helpers
  generateId(): string {
    return Math.random().toString(36).slice(2, 10);
  }

  private cloneColumns(cols: KanbanColumn[]): KanbanColumn[] {
    // Structured clone via JSON is fine here (pure data).
    try {
      return JSON.parse(JSON.stringify(cols));
    } catch {
      return cols.map((c) => ({ ...c, cards: (c.cards ?? []).map((k) => ({ ...k })) }));
    }
  }

  getColumnWidthPx(col: KanbanColumn): number {
    const w = Number(col?.widthPx);
    if (Number.isFinite(w) && w > 0) return w;
    return this.defaultColumnWidthPx;
  }

  private clampWidthPx(w: number): number {
    return Math.max(this.minColumnWidthPx, Math.min(this.maxColumnWidthPx, w));
  }

  onResizeHandlePointerDown(event: PointerEvent, col: KanbanColumn) {
    event.preventDefault();
    event.stopPropagation();

    this.resizingColumnId = col.id;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.getColumnWidthPx(col);
    this.activeResizePointerId = Number.isFinite(event.pointerId) ? event.pointerId : null;

    // Avoid accidental text selection while resizing
    try {
      document.body.style.userSelect = 'none';
    } catch {}

    // Best effort: bind the pointer stream to this element.
    try {
      (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
    } catch {}

    this.resizeMoveListener = (e: PointerEvent) => {
      this.applyResizeClientX(e.clientX);
    };

    this.resizeUpListener = (_e: PointerEvent) => {
      this.endResize(true);
    };
    this.resizeCancelListener = (_e: PointerEvent) => {
      this.endResize(true);
    };
    this.resizeMouseMoveListener = (e: MouseEvent) => {
      this.applyResizeClientX(e.clientX);
    };
    this.resizeMouseUpListener = (_e: MouseEvent) => {
      this.endResize(true);
    };

    document.addEventListener('pointermove', this.resizeMoveListener);
    document.addEventListener('pointerup', this.resizeUpListener, { once: true });
    document.addEventListener('pointercancel', this.resizeCancelListener, { once: true });
    // Fallback path for environments where pointer events are flaky.
    document.addEventListener('mousemove', this.resizeMouseMoveListener);
    document.addEventListener('mouseup', this.resizeMouseUpListener, { once: true });
  }

  private applyResizeClientX(clientX: number) {
    if (!this.resizingColumnId) return;
    const active = this.columns.find((c) => c.id === this.resizingColumnId);
    if (!active) return;
    const next = this.clampWidthPx(this.resizeStartWidth + (clientX - this.resizeStartX));
    active.widthPx = next;
    // Keep drag updates light; persist once on pointer release.
    this.dirty = true;
  }

  private endResize(commit: boolean) {
    if (this.resizeMoveListener) document.removeEventListener('pointermove', this.resizeMoveListener);
    // pointerup/pointercancel/mouseup listeners are once:true, but remove defensively
    if (this.resizeUpListener) document.removeEventListener('pointerup', this.resizeUpListener);
    if (this.resizeCancelListener) document.removeEventListener('pointercancel', this.resizeCancelListener);
    if (this.resizeMouseMoveListener) document.removeEventListener('mousemove', this.resizeMouseMoveListener);
    if (this.resizeMouseUpListener) document.removeEventListener('mouseup', this.resizeMouseUpListener);
    this.resizeMoveListener = undefined;
    this.resizeUpListener = undefined;
    this.resizeCancelListener = undefined;
    this.resizeMouseMoveListener = undefined;
    this.resizeMouseUpListener = undefined;
    this.activeResizePointerId = null;

    try {
      document.body.style.userSelect = '';
    } catch {}

    this.resizingColumnId = null;
    if (commit) this.commit();
  }

  // Column actions
  addColumn() {
    this.columns.push({ id: this.generateId(), title: 'New Column', cards: [] });
    this.commit();
  }

  renameColumn(column: KanbanColumn, newTitle: string) {
    column.title = newTitle.trim() || column.title;
    this.editingColumnId = null;
    this.commit();
  }

  deleteColumn(column: KanbanColumn) {
    this.columns = this.columns.filter(c => c.id !== column.id);
    this.commit();
  }

  moveColumn(fromIndex: number, toIndex: number) {
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || fromIndex >= this.columns.length) return;
    const boundedTarget = Math.max(0, Math.min(this.columns.length, toIndex));
    const [col] = this.columns.splice(fromIndex, 1);
    const insertAt = fromIndex < boundedTarget ? boundedTarget - 1 : boundedTarget;
    this.columns.splice(insertAt, 0, col);
    this.commit();
  }

  // Card actions
  onCardClick(columnId: string, card: KanbanCard) {
    if (!card || this.editingCardId === card.id) return;
    this.selectedCardId = card.id;
    this.selectedColumnId = columnId;
    this.cardSelected.emit({ columnId, card: { ...card } });
  }

  addCard(column: KanbanColumn) {
    // Create draft ticket card; user must click Save in the card editor to commit.
    const id = this.generateId();
    column.cards.push({ id, title: '', description: '' });
    this.draftCardIds.add(id);
    this.editingCardId = id;
    this.selectedCardId = id;
    this.selectedColumnId = column.id;
  }

  editCard(card: KanbanCard, newTitle: string, newDesc: string | undefined) {
    card.title = (newTitle || '').trim() || card.title;
    card.description = (newDesc || '').trim();
    this.editingCardId = null;
    this.draftCardIds.delete(card.id);
    this.commit();
  }

  deleteCard(column: KanbanColumn, cardId: string) {
    column.cards = column.cards.filter(c => c.id !== cardId);
    this.draftCardIds.delete(cardId);
    if (this.selectedCardId === cardId) {
      this.selectedCardId = null;
      this.selectedColumnId = null;
    }
    this.commit();
  }

  cancelCardEdit(column: KanbanColumn, card: KanbanCard) {
    // If it was a new draft card and user cancels, remove it without committing.
    if (this.draftCardIds.has(card.id)) {
      column.cards = column.cards.filter((c) => c.id !== card.id);
      this.draftCardIds.delete(card.id);
    }
    this.editingCardId = null;
  }

  // Native DnD handlers
  private getTransparentDragImage(): HTMLImageElement {
    if (this.transparentDragImage) return this.transparentDragImage;
    const img = new Image();
    // 1x1 transparent gif
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
    this.transparentDragImage = img;
    return img;
  }

  onColumnDragStart(event: DragEvent, index: number) {
    event.stopPropagation();
    event.dataTransfer?.setData('text/column-index', String(index));
    event.dataTransfer?.setDragImage(this.getTransparentDragImage(), 0, 0);
  }

  onColumnDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onColumnDrop(event: DragEvent, toIndex: number) {
    event.preventDefault();
    const fromIndexStr = event.dataTransfer?.getData('text/column-index');
    if (!fromIndexStr) return;
    const fromIndex = Number(fromIndexStr);
    this.moveColumn(fromIndex, toIndex);
  }

  onCardDragStart(event: DragEvent, columnId: string, cardId: string) {
    event.stopPropagation();
    event.dataTransfer?.setData('text/card-id', cardId);
    event.dataTransfer?.setData('text/from-column-id', columnId);
    event.dataTransfer?.setDragImage(this.getTransparentDragImage(), 0, 0);
  }

  onCardDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onCardDrop(event: DragEvent, toColumnId: string, toIndex: number | null = null) {
    event.preventDefault();
    event.stopPropagation();
    const cardId = event.dataTransfer?.getData('text/card-id');
    const fromColumnId = event.dataTransfer?.getData('text/from-column-id');
    if (!cardId || !fromColumnId) return;

    const fromColumn = this.columns.find(c => c.id === fromColumnId);
    const toColumn = this.columns.find(c => c.id === toColumnId);
    if (!fromColumn || !toColumn) return;

    const cardIndex = fromColumn.cards.findIndex(c => c.id === cardId);
    if (cardIndex < 0) return;
    const [card] = fromColumn.cards.splice(cardIndex, 1);

    if (toIndex === null || toIndex === undefined || toIndex < 0 || toIndex > toColumn.cards.length) {
      toColumn.cards.push(card);
    } else {
      toColumn.cards.splice(toIndex, 0, card);
    }

    if (fromColumnId !== toColumnId) {
      this.cardDropped.emit({ card: { ...card }, fromColumnId, toColumnId });
    }
    this.commit();
  }
}


